import { useMemo, useRef, useState } from 'react';
import { CalendarIcon, ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import {
  addMonths,
  eachDayOfInterval,
  endOfMonth,
  endOfWeek,
  format,
  isSameDay,
  isSameMonth,
  isWithinInterval,
  parseISO,
  startOfMonth,
  startOfWeek,
} from 'date-fns';
import { cn } from '@/utils';

interface DateRangePickerProps {
  id?: string;
  label?: string;
  /** 'yyyy-MM-dd', or '' for no bound -- the same shape the two plain
   *  `<input type="date">` boxes this replaces used, so the caller's filter
   *  state and the wire params it feeds are unchanged. */
  fromValue: string;
  toValue: string;
  onChange: (from: string, to: string) => void;
  placeholder?: string;
}

const DAY_LABELS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];

function toKey(day: Date): string {
  return format(day, 'yyyy-MM-dd');
}

function parseKey(value: string): Date | null {
  if (!value) return null;
  try {
    return parseISO(value);
  } catch {
    return null;
  }
}

/**
 * C6: an interactive, click-to-pick calendar in place of two independent
 * `<input type="date">` boxes for "Issued From" / "Issued To".
 *
 * No date-range-picker component existed anywhere in the codebase to reuse
 * (checked: `date-fns` -- a date MATH library -- was already a dependency,
 * but no calendar UI of any kind; every "Calendar" hit elsewhere in `src/` was
 * `CalendarIcon` from heroicons, not a component). Per the batch brief, adding
 * a new UI library for this was not this lane's call to make on its own, so
 * this is built on `date-fns` alone -- already a project dependency -- and no
 * new package is added.
 *
 * Two clicks pick a range: the first sets the start (clearing any previous
 * end), the second sets the end -- swapped into order if it lands before the
 * start, so the pair sent on the wire (`onChange(from, to)`) is never
 * inverted. A third click starts a fresh range rather than extending the old
 * one.
 */
export function DateRangePicker({ id, label, fromValue, toValue, onChange, placeholder }: DateRangePickerProps) {
  const [open, setOpen] = useState(false);
  const fromDate = useMemo(() => parseKey(fromValue), [fromValue]);
  const toDate = useMemo(() => parseKey(toValue), [toValue]);
  const [visibleMonth, setVisibleMonth] = useState(() => fromDate ?? toDate ?? new Date());
  const containerRef = useRef<HTMLDivElement>(null);

  const openPicker = () => {
    // Re-anchor the visible month on whatever is currently applied every time
    // the popover is opened (not on every render -- a picked date should not
    // yank the calendar back under the user's finger while they browse other
    // months mid-selection).
    setVisibleMonth(fromDate ?? toDate ?? new Date());
    setOpen(true);
  };

  const days = useMemo(() => {
    const start = startOfWeek(startOfMonth(visibleMonth));
    const end = endOfWeek(endOfMonth(visibleMonth));
    return eachDayOfInterval({ start, end });
  }, [visibleMonth]);

  const handleDayClick = (day: Date) => {
    // A complete (or empty) range: this click starts a fresh one.
    if (!fromDate || toDate) {
      onChange(toKey(day), '');
      return;
    }
    // One bound already chosen: this click completes the range, ordered so
    // `from` is never after `to` regardless of click order.
    if (day < fromDate) {
      onChange(toKey(day), toKey(fromDate));
    } else {
      onChange(toKey(fromDate), toKey(day));
    }
    setOpen(false);
  };

  const triggerLabel =
    fromValue || toValue
      ? `${fromValue ? format(parseISO(fromValue), 'dd MMM yyyy') : '…'} – ${
          toValue ? format(parseISO(toValue), 'dd MMM yyyy') : '…'
        }`
      : (placeholder ?? 'Select date range');

  return (
    <div className="relative w-full" ref={containerRef}>
      {label && (
        <label htmlFor={id} className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
          {label}
        </label>
      )}
      <button
        type="button"
        id={id}
        onClick={() => (open ? setOpen(false) : openPicker())}
        className={cn(
          'w-full px-3 py-2 border rounded-lg transition-colors duration-200 text-left flex items-center gap-2',
          'focus:outline-none focus:ring-2 focus:ring-primary-500 focus:border-transparent',
          'bg-white dark:bg-gray-800 border-gray-300 dark:border-gray-600',
          'hover:border-gray-400 dark:hover:border-gray-500'
        )}
      >
        <CalendarIcon className="w-4 h-4 text-gray-400 dark:text-gray-500 flex-shrink-0" />
        <span
          className={cn(
            'truncate text-sm',
            fromValue || toValue ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'
          )}
        >
          {triggerLabel}
        </span>
      </button>

      {open && (
        <>
          {/* A full-viewport click-catcher, same purpose as the outside-click
              listener SearchableSelect uses -- but done with a backdrop
              rather than a document listener, so a click that starts a drag
              inside the calendar and releases outside it (a native browser
              gesture on nothing in particular here) cannot be mistaken for an
              "outside click" mid-selection. */}
          <div className="fixed inset-0 z-10" onClick={() => setOpen(false)} aria-hidden="true" />
          <div className="absolute z-20 mt-1 w-72 rounded-lg border border-gray-200 bg-white p-3 shadow-lg dark:border-gray-700 dark:bg-gray-800">
            <div className="mb-2 flex items-center justify-between">
              <button
                type="button"
                aria-label="Previous month"
                onClick={() => setVisibleMonth((m) => addMonths(m, -1))}
                className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronLeftIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
              <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                {format(visibleMonth, 'MMMM yyyy')}
              </span>
              <button
                type="button"
                aria-label="Next month"
                onClick={() => setVisibleMonth((m) => addMonths(m, 1))}
                className="rounded p-1 hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <ChevronRightIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
              </button>
            </div>
            <div className="grid grid-cols-7 gap-y-1 text-center">
              {DAY_LABELS.map((dayLabel, index) => (
                // Sunday and Wednesday both render "S"/"W" once each week, so
                // the label alone is not a stable key -- position is.
                <span key={index} className="text-xs font-medium text-gray-400 dark:text-gray-500">
                  {dayLabel}
                </span>
              ))}
              {days.map((day) => {
                const isStart = fromDate ? isSameDay(day, fromDate) : false;
                const isEnd = toDate ? isSameDay(day, toDate) : false;
                const inRange =
                  fromDate && toDate ? isWithinInterval(day, { start: fromDate, end: toDate }) : false;
                const outsideMonth = !isSameMonth(day, visibleMonth);
                return (
                  <button
                    key={day.toISOString()}
                    type="button"
                    aria-label={format(day, 'PPPP')}
                    aria-pressed={isStart || isEnd}
                    onClick={() => handleDayClick(day)}
                    className={cn(
                      'mx-auto flex h-8 w-8 items-center justify-center rounded-full text-sm transition-colors',
                      outsideMonth && 'text-gray-300 dark:text-gray-600',
                      !outsideMonth && !isStart && !isEnd && 'text-gray-700 dark:text-gray-200',
                      (isStart || isEnd) && 'bg-primary-600 font-medium text-white',
                      inRange && !isStart && !isEnd && 'rounded-none bg-primary-50 dark:bg-primary-900/30',
                      !isStart && !isEnd && 'hover:bg-gray-100 dark:hover:bg-gray-700'
                    )}
                  >
                    {format(day, 'd')}
                  </button>
                );
              })}
            </div>
            {(fromValue || toValue) && (
              <button
                type="button"
                onClick={() => {
                  onChange('', '');
                  setOpen(false);
                }}
                className="mt-2 text-xs font-medium text-primary-600 hover:text-primary-700 dark:hover:text-primary-300 dark:text-primary-400"
              >
                Clear dates
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
